import { useQuery } from '@tanstack/react-query';
import { SymbolDetailsView, useApi } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useWorkspace } from '../workspace/workspaceStore';
import { useDayBar, useSymbols } from './useSymbols';

/** Yesterday's close: the D1 bar before today's (still forming) one. Null until there is one, or on a new symbol. */
function usePrevClose(symbol: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['prev-close', symbol],
    enabled: symbol !== undefined,
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: async (): Promise<number | null> => {
      const { data } = await api.GET('/api/v1/bars', {
        params: { query: { symbol: symbol!, tf: 'D1', count: 2 } },
      });
      return data?.bars[1]?.c ?? null;
    },
  });
}

/**
 * The classic terminal's Market Watch "Details" tab: everything about the symbol currently selected in the Symbols
 * list (bid/ask with their daily high and low, the daily change, and the day's open and previous close), one row
 * per figure, as MT5 shows it — rather than the Symbols list again with more columns.
 */
export function SymbolDetails() {
  const activeSymbol = useWorkspace((s) => s.activeSymbol);
  const symbols = useSymbols().data;
  const symbol = activeSymbol ?? symbols?.[0]?.name;
  const info = symbols?.find((s) => s.name === symbol);
  const quote = useStream((s) => (symbol ? s.quotes[symbol] : undefined));
  const day = useDayBar(symbol ?? '').data ?? null;
  const prevClose = usePrevClose(symbol).data ?? null;

  if (!symbol || !info) {
    return <p className="p-3 text-sm text-muted">No symbol selected.</p>;
  }

  return (
    <SymbolDetailsView
      symbol={symbol}
      description={info.description}
      digits={info.digits}
      quote={quote}
      day={day}
      prevClose={prevClose}
    />
  );
}
